import { communicationService, TrackingBidDefinition } from '@ad-engine/communication';
import {
	AdSlot,
	context,
	DEFAULT_MAX_DELAY,
	pbjsFactory,
	slotService,
	utils,
} from '@ad-engine/core';
import {
	BIDDERS_BIDS_REFRESH,
	BIDDERS_BIDS_RESPONSE,
	DSA_AVAILABLE,
} from '../../communication/events/events-bidders';
import { getSlotNameByBidderAlias } from '../bidder-helper';
import { BidsRefreshing } from '../bidder-provider';
import { intentIQ } from './intent-iq';
import { requestBids } from './prebid-helper';
import { dsaDataAvailable, prepareDsaData } from './utils/dsa';
import { aps } from './aps';

const logGroup = 'prebid-data-tracking';

class PrebidDataTracking {
	private bidsRefreshing: BidsRefreshing = { slots: [], bidsBackHandler: () => void 0 };
	private isConfigured = false;
	private adUnits: PrebidAdUnit[] = [];

	configure(adUnits: PrebidAdUnit[]) {
		if (adUnits && adUnits.length > 0) {
			this.adUnits = [...this.adUnits, ...adUnits];
		}

		if (this.isConfigured) return;
		this.isConfigured = true;

		this.registerBidsRefreshing();
		this.registerBidsTracking();
		this.enableAnalytics();
	}

	private async registerBidsRefreshing(): Promise<void> {
		const pbjs: Pbjs = await pbjsFactory.init();

		this.bidsRefreshing = context.get('bidders.prebid.bidsRefreshing') ?? this.bidsRefreshing;

		const refreshUsedBid = (winningBid) => {
			const isS2sBid = (adUnit: PrebidAdUnit) =>
				adUnit.bids[0].bidder === 'mgnipbs' && winningBid?.source === 's2s';

			if (this.bidsRefreshing.slots.indexOf(winningBid.adUnitCode) !== -1) {
				communicationService.emit(BIDDERS_BIDS_REFRESH, {
					refreshedSlotNames: [winningBid.adUnitCode],
				});

				const adUnitsToRefresh = this.adUnits.filter(
					(adUnit) =>
						adUnit.code === winningBid.adUnitCode &&
						adUnit.bids &&
						adUnit.bids[0] &&
						(adUnit.bids[0].bidder === winningBid.bidderCode || isS2sBid(adUnit)),
				);

				requestBids(adUnitsToRefresh, this.bidsRefreshing.bidsBackHandler, DEFAULT_MAX_DELAY);
			}
		};

		pbjs.onEvent('bidWon', refreshUsedBid);

		utils.logger(logGroup, 'Bids refreshing configured');
	}

	private async registerBidsTracking(): Promise<void> {
		const pbjs: Pbjs = await pbjsFactory.init();

		const trackBid = (response: PrebidBidResponse) => {
			communicationService.emit(BIDDERS_BIDS_RESPONSE, {
				bidResponse: this.mapResponseToTrackingBidDefinition(response),
			});
		};
		const handleDsa = (response: PrebidBidResponse, document: Document | null) => {
			utils.logger(
				logGroup,
				'Checking DSA data in slot ' + this.getRealSlotName(response, document) + ' for bid',
				response,
			);

			if (dsaDataAvailable(response)) {
				const slotName = this.getRealSlotName(response, document);
				const slot = slotService.get(slotName);
				const dsaData = prepareDsaData(response, slot);
				communicationService.emit(DSA_AVAILABLE, dsaData);
			}
		};

		pbjs.onEvent('bidResponse', trackBid);
		pbjs.onEvent(
			'adRenderSucceeded',
			(response: { bid: PrebidBidResponse; doc: Document | null }) => {
				intentIQ.reportPrebidWin(response.bid);

				const slot = Object.values(slotService.slots).find(
					(slot: AdSlot) =>
						(slot.getSlotName() === response?.bid?.adUnitCode ||
							slot?.config?.bidderAlias === response?.bid?.adUnitCode) &&
						slot?.getTargeting()?.hb_adid === response?.bid?.adId,
				);

				if (slot) {
					utils.logger(logGroup, `Ad render succeeded, bid used for slot: ${slot.getSlotName()}`);
					slot.updateWinningPbBidderDetails();
				}

				handleDsa(response.bid, response.doc);
			},
		);

		utils.logger(logGroup, 'Bids tracking configured');
	}

	private enableAnalytics(): void {
		const configs = [aps.getAnalyticsConfig()].filter(Boolean);

		if (configs.length) {
			utils.logger(logGroup, 'Analytics enabled', configs);

			(window as any).pbjs.que.push(() => {
				(window as any).pbjs.enableAnalytics(configs);
			});
		}
	}

	private mapResponseToTrackingBidDefinition(response: PrebidBidResponse): TrackingBidDefinition {
		utils.logger(
			logGroup,
			`Response for ${getSlotNameByBidderAlias(response.adUnitCode)}: ${response.bidderCode} (CPM ${response.cpm.toString()})`,
			response,
		);
		return {
			bidderName: response.bidderCode,
			price: response.cpm.toString(),
			responseTimestamp: response.responseTimestamp,
			slotName: getSlotNameByBidderAlias(response.adUnitCode),
			size: response.size,
			timeToRespond: response.timeToRespond,
			additionalInfo: response?.meta?.dsa ? { dsa: response.meta.dsa } : undefined,
		};
	}

	private getRealSlotName(bid: PrebidBidResponse, doc: Document | null): string {
		const specialSlots = ['incontent_leaderboard', 'incontent_boxad', 'top_boxad'];
		const slotName = getSlotNameByBidderAlias(bid.adUnitCode);
		if (specialSlots.includes(slotName)) {
			// For special slots, such as incontent_leaderboard and incontent_boxad,
			// adUnitCode is always the same while the slots are continuously named
			// incontent_leaderboard, incontent_leaderboard_1, incontent_leaderboard_2, etc.
			// This ugly hack gets the real slot name from id of the ad container itself
			return doc?.defaultView?.frameElement?.parentElement?.parentElement?.id ?? slotName;
		}
		return slotName;
	}
}

export const prebidDataTracking = new PrebidDataTracking();
